import { useMemo } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { AgentSkillsView } from "@common/types/views/agentSkillsView";
import { View } from "@common/types/views/views";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useAgentSkills } from "@api/agentSkill.api";
import { AgentSkillMenuItemKey } from "./agentSkillActions";
import {
    AgentSkillsFilters,
    DEFAULT_AGENT_SKILLS_FILTERS,
    filterAgentSkills,
} from "./agentSkillsFilters";
import { AgentSkillRow, agentSkillsViewConfig } from "./agentSkillsViewConfig";

export interface AgentSkillCallbacks {
    onMenuItemSelect: (selection: {
        key: AgentSkillMenuItemKey;
        skill: AgentSkillRow;
    }) => void;
    onOpen: (skill: AgentSkillRow) => void;
}

interface Props {
    clientWorkspaceId: string | null;
    filters: AgentSkillsFilters;
    view: AgentSkillsView;
    canManage: boolean;
    callbacks: AgentSkillCallbacks;
    onViewChange: (view: View) => void;
    onClearFilters: () => void;
    onCreate: () => void;
}

export const AgentSkillsList = ({
    clientWorkspaceId,
    filters,
    view,
    canManage,
    callbacks,
    onViewChange,
    onClearFilters,
    onCreate,
}: Props) => {
    const {
        data: skills,
        isFetched,
        isError,
        refetch,
    } = useAgentSkills(clientWorkspaceId ? { clientWorkspaceId } : {});

    const rows = useMemo<AgentSkillRow[]>(
        () => filterAgentSkills({ skills: skills ?? [], filters }),
        [skills, filters],
    );
    const hasFilters =
        filters.search !== DEFAULT_AGENT_SKILLS_FILTERS.search ||
        filters.category !== DEFAULT_AGENT_SKILLS_FILTERS.category ||
        filters.enabled !== DEFAULT_AGENT_SKILLS_FILTERS.enabled;

    const tableMeta = useMemo<
        ViewTableMeta<AgentSkillCallbacks, { canManage: boolean }>
    >(() => ({ callbacks, options: { canManage } }), [callbacks, canManage]);

    return (
        <ViewTable
            data={isFetched ? rows : null}
            view={view}
            config={agentSkillsViewConfig}
            onViewChange={onViewChange}
            onRowClick={(row) => {
                if (row) {
                    callbacks.onOpen(row);
                }
            }}
            emptyView={() => {
                if (isError) {
                    return (
                        <EmptyState
                            header="Failed to load skills"
                            ctaText="Retry"
                            action={() => void refetch()}
                        />
                    );
                }
                if (hasFilters) {
                    return (
                        <EmptyState
                            header="No skills found"
                            body="No skills match your search or filters."
                            ctaText="Clear filters"
                            action={onClearFilters}
                        />
                    );
                }
                return (
                    <EmptyState
                        header="No skills yet"
                        body="Create one to give the agent your firm's guidance."
                        ctaText={canManage ? "Create skill" : undefined}
                        action={canManage ? onCreate : undefined}
                    />
                );
            }}
            loadingView={() => <Loader />}
            tableMeta={tableMeta}
            fixedTopOffsetPx
            enableColumnResizing
            footer={
                <CommandIsland
                    sticky
                    className={commandIslandStyles.bulkActionsStickyFooter}
                >
                    {rows.length > 0 ? (
                        <CommandIslandTotal>
                            {pluralizeWithCount("skill", rows.length)}
                        </CommandIslandTotal>
                    ) : undefined}
                </CommandIsland>
            }
        />
    );
};
